import { Injectable } from '@angular/core';
import {
    ArcGISFeature,
    ArcGISExtent,
    ArcGISGeometry,
    ServiceTokenContext,
    PBFCapabilities,
    FetchStrategy,
    SpatialGridCell,
    QuantizationParameters,
    GeoJSONFeature,
    GeoJSONGeometry,
    PBFParseResult,
    GEOJSON_GEOMETRY_TYPES
} from '../models/arcgis-types';
import { ArcGISTokenManagerService } from './arcgis-token-manager.service';
import { ARCGIS_CONSTANTS } from '../constants/arcgis-constants';

@Injectable({
    providedIn: 'root'
})
export class ArcGISOptimizedFetcherService {
    private serviceUrl: string = '';
    private tokenContext: ServiceTokenContext | undefined;
    private pbfFailedServices = new Set<string>();

    constructor(
        private arcgisTokenManager: ArcGISTokenManagerService
    ) {}

    public async fetchAllFeatures(
        serviceUrl: string,
        layerId: number | string,
        context?: ServiceTokenContext,
        pbfCapabilities?: PBFCapabilities,
        layerExtent?: ArcGISExtent | null,
        signal?: AbortSignal
    ): Promise<ArcGISFeature[]> {
        this.serviceUrl = serviceUrl;
        this.tokenContext = context;

        try {
            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const totalCount = await this.getTotalCount(serviceUrl, layerId, signal);

            if (totalCount === 0) {
                return [];
            }

            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const strategy = this.determineStrategy(totalCount, pbfCapabilities, serviceUrl);

            switch (strategy) {
                case ARCGIS_CONSTANTS.FETCH_STRATEGY.SIMPLE:
                    return await this.fetchSimple(serviceUrl, layerId, signal);

                case ARCGIS_CONSTANTS.FETCH_STRATEGY.PBF_TILED:
                    try {
                        return await this.fetchPBFTiled(serviceUrl, layerId, totalCount, layerExtent, signal);
                    } catch (error) {
                        if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                            throw error;
                        }
                        this.pbfFailedServices.add(serviceUrl);
                        return await this.fetchJSONThrottled(serviceUrl, layerId, totalCount, signal);
                    }

                case ARCGIS_CONSTANTS.FETCH_STRATEGY.JSON_THROTTLED:
                default:
                    return await this.fetchJSONThrottled(serviceUrl, layerId, totalCount, signal);
            }

        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                throw error;
            }
            return [];
        }
    }

    public clearPBFFailureCache(): void {
        this.pbfFailedServices.clear();
    }

    private determineStrategy(
        featureCount: number,
        pbfCapabilities: PBFCapabilities | undefined,
        serviceUrl: string
    ): FetchStrategy {
        if (featureCount <= ARCGIS_CONSTANTS.FETCH_THRESHOLDS.SIMPLE_FETCH_MAX) {
            return ARCGIS_CONSTANTS.FETCH_STRATEGY.SIMPLE;
        }

        if (this.pbfFailedServices.has(serviceUrl)) {
            return ARCGIS_CONSTANTS.FETCH_STRATEGY.JSON_THROTTLED;
        }

        if (pbfCapabilities?.supportsPBF) {
            return ARCGIS_CONSTANTS.FETCH_STRATEGY.PBF_TILED;
        }

        return ARCGIS_CONSTANTS.FETCH_STRATEGY.JSON_THROTTLED;
    }

    private async fetchSimple(serviceUrl: string, layerId: number | string, signal?: AbortSignal): Promise<ArcGISFeature[]> {
        const queryUrl = serviceUrl + '/' + layerId.toString() + '/query';
        const response = await this.arcgisTokenManager.fetchWithAuthRetry(
            force => {
                const params = new URLSearchParams({
                    where: ARCGIS_CONSTANTS.QUERY_DEFAULTS.WHERE_ALL,
                    outFields: ARCGIS_CONSTANTS.QUERY_DEFAULTS.OUT_FIELDS_ALL,
                    returnGeometry: ARCGIS_CONSTANTS.QUERY_DEFAULTS.RETURN_GEOMETRY_TRUE,
                    f: ARCGIS_CONSTANTS.FORMAT.JSON,
                    outSR: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR
                });
                this.appendCorrectTokenToParams(params, serviceUrl, force);
                return queryUrl + '?' + params.toString();
            },
            { signal }
        );
        const data = await response.json();

        return data.features || [];
    }

    private async fetchPBFTiled(
        serviceUrl: string,
        layerId: number | string,
        totalCount: number,
        layerExtent: ArcGISExtent | null | undefined,
        signal?: AbortSignal
    ): Promise<ArcGISFeature[]> {
        const extent = layerExtent || await this.fetchLayerExtent(serviceUrl, layerId, signal);
        if (!extent) {
            throw new Error('Cannot fetch PBF without layer extent');
        }

        if (signal?.aborted) {
            throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
        }

        const gridSize = this.calculateGridSize(totalCount);
        const gridCells = this.createSpatialGrid(extent, gridSize);
        const quantizationParams = this.buildQuantizationParameters(extent);
        const maxAllowableOffset = this.calculateMaxAllowableOffset(extent);

        const tileTasks = gridCells.map(cell => () =>
            this.fetchPBFTile(serviceUrl, layerId, cell, quantizationParams, maxAllowableOffset, signal)
        );

        const tileResults = await this.executeWithConcurrencyLimit(
            tileTasks,
            ARCGIS_CONSTANTS.CONCURRENCY.PBF_TILES,
            signal
        );

        const allGeoJSONFeatures = tileResults.flat();
        const uniqueFeatures = this.deduplicateGeoJSONFeatures(allGeoJSONFeatures);

        return this.convertGeoJSONToEsri(uniqueFeatures);
    }

    private async fetchPBFTile(
        serviceUrl: string,
        layerId: number | string,
        bbox: SpatialGridCell,
        quantizationParams: QuantizationParameters,
        maxAllowableOffset: number,
        signal?: AbortSignal
    ): Promise<GeoJSONFeature[]> {
        const queryUrl = serviceUrl + '/' + layerId.toString() + '/query';

        const geometryEnvelope = {
            xmin: bbox.xmin,
            ymin: bbox.ymin,
            xmax: bbox.xmax,
            ymax: bbox.ymax,
            spatialReference: { wkid: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR_WKID }
        };

        const response = await this.arcgisTokenManager.fetchWithAuthRetry(
            force => {
                const params = new URLSearchParams({
                    f: ARCGIS_CONSTANTS.PBF.FORMAT,
                    where: ARCGIS_CONSTANTS.QUERY_DEFAULTS.WHERE_ALL,
                    outFields: ARCGIS_CONSTANTS.QUERY_DEFAULTS.OUT_FIELDS_ALL,
                    returnGeometry: ARCGIS_CONSTANTS.QUERY_DEFAULTS.RETURN_GEOMETRY_TRUE,
                    outSR: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR,
                    inSR: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR,
                    geometry: JSON.stringify(geometryEnvelope),
                    geometryType: ARCGIS_CONSTANTS.PBF.GEOMETRY_TYPE_ENVELOPE,
                    spatialRel: ARCGIS_CONSTANTS.PBF.SPATIAL_REL_INTERSECTS,
                    resultType: ARCGIS_CONSTANTS.PBF.RESULT_TYPE_TILE,
                    maxAllowableOffset: maxAllowableOffset.toString(),
                    quantizationParameters: JSON.stringify(quantizationParams)
                });
                this.appendCorrectTokenToParams(params, serviceUrl, force);
                return queryUrl + '?' + params.toString();
            },
            { signal }
        );

        if (!response.ok) {
            throw new Error('PBF fetch failed: ' + response.status.toString() + ' ' + response.statusText);
        }

        const buffer = await response.arrayBuffer();
        return await this.parsePBFResponse(buffer);
    }

    private async parsePBFResponse(buffer: ArrayBuffer): Promise<GeoJSONFeature[]> {
        const arcgisPbfDecode = (await import('arcgis-pbf-parser')).default as (data: Uint8Array) => PBFParseResult;
        const uint8Array = new Uint8Array(buffer);
        const result = arcgisPbfDecode(uint8Array);

        if (result.featureCollection?.features) {
            return result.featureCollection.features;
        }

        return [];
    }

    private async fetchJSONThrottled(
        serviceUrl: string,
        layerId: number | string,
        totalCount: number,
        signal?: AbortSignal
    ): Promise<ArcGISFeature[]> {
        const recordCount = ARCGIS_CONSTANTS.CONCURRENCY.CHUNK_SIZE;
        const numChunks = Math.ceil(totalCount / recordCount);

        const chunkTasks: Array<() => Promise<ArcGISFeature[]>> = [];
        for (let i = 0; i < numChunks; i++) {
            const offset = i * recordCount;
            chunkTasks.push(() => this.fetchJSONChunk(serviceUrl, layerId, offset, recordCount, signal));
        }

        const chunks = await this.executeWithConcurrencyLimit(
            chunkTasks,
            ARCGIS_CONSTANTS.CONCURRENCY.JSON_CHUNKS,
            signal
        );

        return chunks.flat();
    }

    private async fetchJSONChunk(
        serviceUrl: string,
        layerId: number | string,
        offset: number,
        recordCount: number,
        signal?: AbortSignal
    ): Promise<ArcGISFeature[]> {
        const queryUrl = serviceUrl + '/' + layerId.toString() + '/query';

        try {
            const response = await this.arcgisTokenManager.fetchWithAuthRetry(
                force => {
                    const params = new URLSearchParams({
                        where: ARCGIS_CONSTANTS.QUERY_DEFAULTS.WHERE_ALL,
                        outFields: ARCGIS_CONSTANTS.QUERY_DEFAULTS.OUT_FIELDS_ALL,
                        returnGeometry: ARCGIS_CONSTANTS.QUERY_DEFAULTS.RETURN_GEOMETRY_TRUE,
                        f: ARCGIS_CONSTANTS.FORMAT.JSON,
                        outSR: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR,
                        resultOffset: offset.toString(),
                        resultRecordCount: recordCount.toString()
                    });
                    this.appendCorrectTokenToParams(params, serviceUrl, force);
                    return queryUrl + '?' + params.toString();
                },
                { signal }
            );
            const data = await response.json();
            return data.features || [];
        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                throw error;
            }
            return [];
        }
    }

    private async executeWithConcurrencyLimit<T>(
        tasks: Array<() => Promise<T>>,
        limit: number,
        signal?: AbortSignal
    ): Promise<T[]> {
        const results: T[] = [];
        const executing: Set<Promise<void>> = new Set();

        for (const task of tasks) {
            if (signal?.aborted) {
                throw new DOMException(ARCGIS_CONSTANTS.ABORT_ERROR.MESSAGE_ABORTED, ARCGIS_CONSTANTS.ABORT_ERROR.NAME);
            }

            const promise = task()
                .then(result => {
                    if (!signal?.aborted) {
                        results.push(result);
                    }
                })
                .catch((error) => {
                    if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                        throw error;
                    }
                })
                .finally(() => {
                    executing.delete(promise);
                });

            executing.add(promise);

            if (executing.size >= limit) {
                try {
                    await Promise.race(executing);
                } catch (error) {
                    if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                        throw error;
                    }
                }
            }
        }

        try {
            await Promise.all(executing);
        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                throw error;
            }
        }
        return results;
    }

    private calculateGridSize(featureCount: number): number {
        if (featureCount < 10000) {
            return ARCGIS_CONSTANTS.GRID_SIZES.SMALL;
        }
        if (featureCount < 50000) {
            return ARCGIS_CONSTANTS.GRID_SIZES.MEDIUM;
        }
        if (featureCount < 100000) {
            return ARCGIS_CONSTANTS.GRID_SIZES.LARGE;
        }
        return ARCGIS_CONSTANTS.GRID_SIZES.XLARGE;
    }

    private createSpatialGrid(extent: ArcGISExtent, gridSize: number): SpatialGridCell[] {
        const cells: SpatialGridCell[] = [];
        const cellWidth = (extent.xmax - extent.xmin) / gridSize;
        const cellHeight = (extent.ymax - extent.ymin) / gridSize;

        for (let row = 0; row < gridSize; row++) {
            for (let col = 0; col < gridSize; col++) {
                cells.push({
                    xmin: extent.xmin + (col * cellWidth),
                    ymin: extent.ymin + (row * cellHeight),
                    xmax: extent.xmin + ((col + 1) * cellWidth),
                    ymax: extent.ymin + ((row + 1) * cellHeight)
                });
            }
        }

        return cells;
    }

    private buildQuantizationParameters(extent: ArcGISExtent): QuantizationParameters {
        const tolerance = this.calculateQuantizationTolerance(extent);

        return {
            mode: ARCGIS_CONSTANTS.QUANTIZATION.MODE_VIEW,
            originPosition: ARCGIS_CONSTANTS.QUANTIZATION.ORIGIN_UPPER_LEFT,
            tolerance: tolerance,
            extent: {
                xmin: extent.xmin,
                ymin: extent.ymin,
                xmax: extent.xmax,
                ymax: extent.ymax,
                spatialReference: extent.spatialReference || { wkid: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR_WKID }
            }
        };
    }

    private calculateQuantizationTolerance(extent: ArcGISExtent): number {
        const extentWidth = extent.xmax - extent.xmin;
        const extentHeight = extent.ymax - extent.ymin;
        const maxDimension = Math.max(extentWidth, extentHeight);
        return maxDimension / 10000;
    }

    private calculateMaxAllowableOffset(extent: ArcGISExtent, pixelTolerance: number = 2): number {
        const extentWidth = extent.xmax - extent.xmin;
        const extentHeight = extent.ymax - extent.ymin;
        const maxDimension = Math.max(extentWidth, extentHeight);
        return (maxDimension / 1000) * pixelTolerance;
    }

    private deduplicateGeoJSONFeatures(features: GeoJSONFeature[]): GeoJSONFeature[] {
        const seen = new Set<string | number>();
        return features.filter(feature => {
            const objectId = feature.properties?.OBJECTID as string | number | undefined;
            const fid = feature.properties?.FID as string | number | undefined;
            const id = objectId || fid || feature.id;

            if (id === undefined || id === null) {
                return true;
            }
            if (seen.has(id)) {
                return false;
            }
            seen.add(id);
            return true;
        });
    }

    private convertGeoJSONToEsri(geoJSONFeatures: GeoJSONFeature[]): ArcGISFeature[] {
        return geoJSONFeatures.map(feature => ({
            attributes: feature.properties as Record<string, string | number | boolean | null | unknown>,
            geometry: this.convertGeoJSONGeometryToEsri(feature.geometry)
        }));
    }

    private convertGeoJSONGeometryToEsri(geojsonGeom: GeoJSONGeometry | null): ArcGISGeometry | undefined {
        if (!geojsonGeom) {
            return undefined;
        }

        const spatialReference = { wkid: ARCGIS_CONSTANTS.SPATIAL_REFERENCE.WEB_MERCATOR_WKID };

        switch (geojsonGeom.type) {
            case GEOJSON_GEOMETRY_TYPES.POINT: {
                const coords = geojsonGeom.coordinates as number[];
                return {
                    x: coords[0],
                    y: coords[1],
                    spatialReference: spatialReference
                };
            }
            case GEOJSON_GEOMETRY_TYPES.MULTI_POINT: {
                const points = geojsonGeom.coordinates as number[][];
                return {
                    points: points,
                    spatialReference: spatialReference
                };
            }
            case GEOJSON_GEOMETRY_TYPES.LINE_STRING: {
                const path = geojsonGeom.coordinates as number[][];
                return {
                    paths: [path],
                    spatialReference: spatialReference
                };
            }
            case GEOJSON_GEOMETRY_TYPES.MULTI_LINE_STRING: {
                const paths = geojsonGeom.coordinates as number[][][];
                return {
                    paths: paths,
                    spatialReference: spatialReference
                };
            }
            case GEOJSON_GEOMETRY_TYPES.POLYGON: {
                const rings = geojsonGeom.coordinates as number[][][];
                return {
                    rings: rings,
                    spatialReference: spatialReference
                };
            }
            case GEOJSON_GEOMETRY_TYPES.MULTI_POLYGON: {
                const multiRings = geojsonGeom.coordinates as number[][][][];
                const flattenedRings: number[][][] = [];
                for (const polygon of multiRings) {
                    for (const ring of polygon) {
                        flattenedRings.push(ring);
                    }
                }
                return {
                    rings: flattenedRings,
                    spatialReference: spatialReference
                };
            }
            default:
                return undefined;
        }
    }

    private async fetchLayerExtent(serviceUrl: string, layerId: number | string, signal?: AbortSignal): Promise<ArcGISExtent | null> {
        const layerUrl = serviceUrl + '/' + layerId.toString();

        try {
            const response = await this.arcgisTokenManager.fetchWithAuthRetry(
                force => {
                    const params = new URLSearchParams({
                        f: ARCGIS_CONSTANTS.FORMAT.JSON
                    });
                    this.appendCorrectTokenToParams(params, serviceUrl, force);
                    return layerUrl + '?' + params.toString();
                },
                { signal }
            );
            const data = await response.json();
            return data.extent || null;
        } catch (error) {
            if ((error as DOMException).name === ARCGIS_CONSTANTS.ABORT_ERROR.NAME) {
                throw error;
            }
            return null;
        }
    }

    private async getTotalCount(serviceUrl: string, layerId: number | string, signal?: AbortSignal): Promise<number> {
        const queryUrl = serviceUrl + '/' + layerId.toString() + '/query';

        const response = await this.arcgisTokenManager.fetchWithAuthRetry(
            force => {
                const params = new URLSearchParams({
                    where: ARCGIS_CONSTANTS.QUERY_DEFAULTS.WHERE_ALL,
                    returnCountOnly: ARCGIS_CONSTANTS.QUERY_DEFAULTS.RETURN_COUNT_ONLY_TRUE,
                    f: ARCGIS_CONSTANTS.FORMAT.JSON
                });
                this.appendCorrectTokenToParams(params, serviceUrl, force);
                return queryUrl + '?' + params.toString();
            },
            { signal }
        );
        const data = await response.json();
        return data.count || 0;
    }

    private appendCorrectTokenToParams(params: URLSearchParams, serviceUrl: string, forceSendToken = false): void {
        this.arcgisTokenManager.appendTokenToParams(params, serviceUrl, this.tokenContext, forceSendToken);
    }
}
